// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Namespaced query keys so caches are easy to invalidate/inspect. The app's
// single QueryClient instance lives in config/queryClient.ts (ADR-0008).

export const queryKeys = {
  /** AEM alarm-api list (backend path). */
  alarms: (siteId: string, filters?: unknown) => ['alarms', siteId, filters ?? {}] as const,
  /**
   * One alarm, narrowed by the columns the detail route carries.
   *
   * `alarm_code` is the RULE, not a row — the same code repeats across every
   * piece of equipment that fires it — so keying on it alone made two different
   * alarms share a cache entry, and the second one opened rendered the first
   * one's row. `equipmentId`/`alarmId` are appended (not inserted) so the
   * `['alarm', siteId]` prefix invalidations in useAlarmMutation still match.
   */
  alarm: (siteId?: string, alarmCode?: string, equipmentId?: string, alarmId?: string) =>
    ['alarm', siteId ?? 'all', alarmCode ?? '', equipmentId ?? '', alarmId ?? ''] as const,
  /** Operator-console alarm set. */
  consoleAlarms: (siteId?: string) => ['console-alarms', siteId ?? 'all'] as const,
  /** Anomalies list (BFF path; upstream AEM calls these findings). */
  anomalies: (siteId?: string) => ['anomalies', siteId ?? 'all'] as const,
  /** One anomaly, narrowed by the columns the detail route carries. */
  anomaly: (siteId?: string, alarmCode?: string, equipmentId?: string, alarmId?: string) =>
    ['anomaly', siteId ?? 'all', alarmCode ?? '', equipmentId ?? '', alarmId ?? ''] as const,
  /** EnergyFluo fault + root-cause brief for one alarm or anomaly. */
  alarmBrief: (
    eventType: 'alarm' | 'anomaly',
    siteId?: string,
    alarmCode?: string,
    alarmId?: string,
    sopIds?: string[],
    ruleTitle?: string,
    category?: string,
    priority?: string,
    controlLayer?: string
  ) =>
    [
      'alarm-brief',
      eventType,
      siteId ?? '',
      alarmCode ?? '',
      alarmId ?? '',
      sopIds ?? [],
      ruleTitle ?? '',
      category ?? '',
      priority ?? '',
      controlLayer ?? '',
    ] as const,
};
